/* eslint-disable object-curly-newline */
/* global G, React */
import { Box, Grid, Collapse } from '@mui/material'
import ErrorBoundary from 'ui/Error'
import { preload, useMemoRef, useStyles } from '@platform/react/hook'
import { memo, Suspense, useContext, useRef, useState } from 'react'
import ApplicationContext from '@platform/react/context/application'
import ChildrenHOC from '@platform/react/hoc/children'
import { styles } from 'ui/Element/Message/List'
import useEventHandler from '@platform/react/hook/useEventHandler'
import useEventCallback from '@platform/react/hook/useEventCallback'
import { getFirstItem } from 'lib/util'
import Alert from 'ui/Element/Text/Alert'

export interface ListContainerProps {
  /** the lazy-loaded child List element */
  childrenObject: { List: any }
  /** the component's event handlers */
  events?: { onFocus?: (arg: null) => void }
  /** value to be set through an action or an event */
  value?: { userId?: string, fullHeight?: boolean }
  /** text for the new-message indicator */
  newMessageLabel?: string
  /** text for the new-message action */
  newMessageActionLabel?: string
  /** any remaining props */
  [key: string]: unknown
}

/**
 * Renders the message list together with the "new message" indicator.
 *
 * @param props the component's properties
 * @returns     the child list element
 */
const ListContainer = memo((props: ListContainerProps) => {
  const { eventBus } = useContext(ApplicationContext) as any
  const classes = useStyles(styles)() as Record<string, string>
  const atBottomRef = useRef(true)
  const [alertVisible, setAlertVisible] = useState(false)
  const { List } = props.childrenObject

  const markReadIfSeen = useEventCallback(() => {
    if (atBottomRef.current && document.hasFocus()) props.events?.onFocus?.(null)
  })

  const handleAtBottom = useEventCallback((atBottom: boolean) => {
    atBottomRef.current = atBottom
    if (atBottom) {
      setAlertVisible(false)
      markReadIfSeen()
    }
  })

  // Incoming WS message
  useEventHandler(
    eventBus,
    eventBus.type(G.MESSAGE, G.ADD),
    useEventCallback((e: any) => {
      const { [G.DATA]: { value: mValue = {}, refs: mRefs = {} } = { } } = e?.detail || e || {}
      const isEditedOrDeleted = mValue?.editTimestamp || mValue?.deleteTimestamp
      const isOwnMessage = getFirstItem(mRefs?.submitter)?.key === props.value?.userId

      if (isEditedOrDeleted || isOwnMessage) return

      atBottomRef.current ? markReadIfSeen() : setAlertVisible(true)
    }),
  )

  // Window regained focus
  useEventHandler(window, 'focus', markReadIfSeen)

  const scrollToBottom = useEventCallback(() => {
    eventBus.dispatch(eventBus.type(G.MESSAGE, G.SCROLL))
  })

  return (
    <>
      <Box className={classes.alertBox}>
        <Collapse in={alertVisible}>
          <Alert
            showAction={true}
            textColor={'black.main'}
            iconColor={'pending.700'}
            value={props.newMessageLabel}
            events={{ onClick: scrollToBottom }}
            actionLabel={props.newMessageActionLabel}
          />
        </Collapse>
      </Box>
      <Box
        style={{
          display: 'flex',
          height: '100%',
          width: '100%',
          alignItems: 'flex-start',
        }}
      >
        <List
          { ...props }
          onAtBottom={handleAtBottom}
        />
      </Box>
    </>
  )
})

/**
 * MessagePane component.
 *
 * Loads the dynamic components and data and passes them to the child list component.
 *
 * @param props        the component's properties
 * @param props.events events for the pane
 * @returns            the new MessagePane element
 */
const MessagePane = ({ events, ...props }: any) => {
  const classes = useStyles(styles)() as Record<string, string>

  return (
    <Box className={classes.container}>
      <ErrorBoundary>
        <Suspense fallback={
          <Grid className={classes.root} />
        }>
          <ListContainer
            {...props}
            events={events}
          />
        </Suspense>
      </ErrorBoundary>
    </Box>
  )
}

export default useMemoRef(ChildrenHOC(MessagePane), (props: any) => [props.value.fullHeight])
